// @ts-strict-ignore
import * as React from 'react';

import {COHORT_METRIC_ENTRY_DEFAULT_PROPS} from 'common/report/util/cohorts/transformations/entries';
import {emitTrackEvent} from 'common/util/dom';
import {IncludeCustomPropertyType} from 'common/query-builder/query-block/properties-menu/types';
import {isCompanyProfileEnabled} from 'common/report/util/feature-sampling';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {CurrentProjectResource} from 'common/report/model-resources/projects';

import {QueryBuilderUpdateDetail} from '../query-builder/types';
import {
  CohortGroupEntry,
  RowLabelType,
  RowTypeComplexOption,
  SelectorTypeDefinitions,
} from '../query-entry-section/types';
import {QueryBuilderReact} from '../query-builder';
import {AGGREGATE_USER_FREQ_BY_GROUP} from '../query-block/properties-menu/constants';
import {Cohort} from './models/cohort';
import {SerializedCohort} from './models/types/serialized-cohort-types';
import {StagedCohort} from './models/builder/staged-cohort';
import {SerializedStagedCohort} from './models/types/serialized-staged-cohort-types';
import {CohortsQueryBuilderUpdate} from './types';

interface CohortsQueryBuilderJsxProps {
  elref?: string;
  cohort?: SerializedCohort;
  /** @todo [pablo] drop this to remove backwards compatibility with site calls using staged-cohort */
  // the serialized cohort you want this builder to render
  stagedCohort?: SerializedStagedCohort;
  projectId: number;
  projectHasCohorts?: boolean;
  dataGroupId?: string;
  supportB2bCohorts?: boolean;
  disabled?: boolean;
  trackName?: string;
  userId?: number;
  workspaceId?: number;
  projectAsOfDate?: string;
  maxDataHistoryDays?: string;
  projectHasPeople?: boolean;
  hideResetButton?: boolean;
  nonCollapsible: boolean;
  hideEditControls?: boolean;
  querySource?: string;
  headerTitle?: string;
  footerItems?: React.ReactNode;
  onCohortUpdate?: (update: CohortsQueryBuilderUpdate) => void;
  onTrack?: (trackedEvent: {eventName: string; props: {[key: string]: any}}) => void;
  currentProject: CurrentProjectResource;
}

type CohortsQueryBuilderState = EmptyObj;

export class CohortsQueryBuilder extends React.PureComponent<CohortsQueryBuilderJsxProps, CohortsQueryBuilderState> {
  private divRef = React.createRef<HTMLDivElement>();

  hasPeopleEnabled() {
    return this.props.currentProject.get().value?.hasIntegratedEngage;
  }

  getProjectAsOfDate() {
    return this.props.currentProject.get().value?.projectAsOfDate ?? ``;
  }

  get cohort() {
    const defaultCohort = Cohort.createCohortFromStaged(StagedCohort.deserialize(null)).serialize();
    return Cohort.parseCohort(this.props.cohort ?? defaultCohort);
  }

  get rowTypes(): RowTypeComplexOption[] {
    return [
      {
        value: SelectorTypeDefinitions.CohortGroup,
        placeholderEntry: {event: COHORT_METRIC_ENTRY_DEFAULT_PROPS.event},
      },
    ];
  }

  /**
   * Updates the selected cohort with the updated groups from the qb
   */
  handleQueryUpdate(detail: QueryBuilderUpdateDetail) {
    let cohort = this.cohort;
    cohort.groups = detail.entries as CohortGroupEntry[];

    // prevent an empty cohort definition from being constructed
    if (cohort.groups.length === 0) {
      cohort = new Cohort({dataGroupId: this.props.dataGroupId});
    }

    this.props.onCohortUpdate?.({
      cohort,
      queryReason: detail.queryReason,
    });
    this.trackUpdate(cohort);
  }

  trackUpdate(cohort: Cohort) {
    const trackingEvent = {
      eventName: `[Cohorts] Update cohort`,
      props: {
        ...cohort.toTrackingData(),
        // Track Query Source for sessions
        ...(this.props.querySource
          ? {
              'Query Source': this.props.querySource,
            }
          : {}),
      },
    };
    if (this.props.onTrack) {
      this.props.onTrack(trackingEvent);
    } else {
      emitTrackEvent(this.divRef.current, trackingEvent);
    }
  }

  render() {
    return (
      <div ref={this.divRef}>
        <QueryBuilderReact
          elref='cohortQueryBuilder'
          contained
          entries={this.cohort.groups}
          addEntryButtonLabel={({t}) => t(`filters.group`)}
          otherFilterOptions={[
            ...(this.cohort.dataGroupId && isCompanyProfileEnabled(this.props.currentProject.get().value)
              ? [AGGREGATE_USER_FREQ_BY_GROUP]
              : []),
          ]}
          // allows for per-line cohort filters as Insights does
          allowCohortFilter
          entryRowLabelType={RowLabelType.None}
          entriesDraggable
          includeCustomProperties={IncludeCustomPropertyType.All}
          showBorrowedProperties
          showAnyEvent
          showSessions
          mainEntryNonCollapsible={this.props.nonCollapsible}
          rowLimit={26}
          /** disables new group on empty state */
          disableAddEntry={this.cohort.groups.length === 1 && this.cohort.groups[0].filters.length === 0}
          /**
           * @todo [pablo] figure out how to slot in save controls content from this project:
           * https://www.notion.so/mpspeed/Design-proposal-v2-e04e0ec0edad422899eaee6372f195f3
           * */
          headerTitle={this.props.headerTitle}
          headerTitleLowercase
          hideDatePicker
          hideEditControls={this.props.hideEditControls}
          hideFilter
          hideGroupBy
          behaviorMode
          /** @todo [pablo] enable this and have b2b validation be entirely owned by the query builder */
          hideDataGroupsSelector
          allDataGroups
          dataGroupId={this.cohort.dataGroupId ?? ``}
          dataGroupsEnabled={this.props.supportB2bCohorts}
          maxDataHistoryDays={+this.props.maxDataHistoryDays}
          rowTypes={this.rowTypes}
          supportB2bCohorts={this.props.supportB2bCohorts}
          projectHasCohorts={this.props.projectHasCohorts}
          inlineCohortsOverride={{enable: false}}
          trackName={`cohorts`}
          projectHasPeople={this.hasPeopleEnabled()}
          projectAsOfDate={this.getProjectAsOfDate()}
          projectId={this.props.projectId}
          workspaceId={this.props.workspaceId}
          userId={this.props.userId}
          footerItems={this.props.footerItems}
          onUpdate={(detail) => this.handleQueryUpdate(detail)}
        />
      </div>
    );
  }
}

export const CohortsBuilderReact = (props: Omit<CohortsQueryBuilderJsxProps, 'currentProject'>) => {
  const currentProject = useCurrentProject();
  return <CohortsQueryBuilder {...props} currentProject={currentProject} />;
};
